<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { FilterMatchMode, FilterOperator, FilterService } from '@primevue/core/api';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { LANG_ENGLISH, STATUS_CLOSED, STATUS_OPEN } from '@/constants';
import { useConvertTextComposable } from '@/components/modal/useConvertTextComposable';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useTourManagementStore } from '@/store/TourManagementStore';
import { useViewComposable } from '@/views/composable';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import capitalize from 'lodash/capitalize';
import DataTable from 'primevue/datatable';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import Tag from 'primevue/tag';
import ToggleSwitch from 'primevue/toggleswitch';
import ActionButtons from '@/components/ActionButtons.vue';
import AddManualTourModal from '@/components/modal/AddManualTourModal.vue';
import DeleteButton from '@/components/DeleteButton.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ProgressModal from '@/components/modal/ProgressModal.vue';
import SeeMoreToursDescription from '@/components/modal/tour/SeeMoreToursDescription.vue';
import TourNotes from '@/components/tour/TourNotes.vue';
import VersionRevertTourDetail from '@/components/tour/VersionRevertTourDetail.vue';
import ViewDataTourDetailModal from '@/components/modal/tour/ViewDataTourDetailModal.vue';

const props = defineProps<{
  tourDetailsData: any[];
  groupTypeList: any[];
  isView?: boolean;
}>();

const emits = defineEmits<{ initTableData: [] }>();

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();
const langCapitalize = capitalize(currentLanguage);
const { styleColumnExport, renderNameFileExport, formatColumnExport, formatDataExport } = useViewComposable();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;

const dialog = useDialog();
const toast = useToast();
const { apiOrder } = useViewComposable();
const { handleReplaceValueToName } = useConvertTextComposable();
const tourManagementStore = useTourManagementStore();

const rowsPerPageOptions = ref([10, 25, 50, 100, 200, 400, 500]);
const statuses = ref([STATUS_OPEN, STATUS_CLOSED]);
const maxLengthText = 25;
const deletedStatuses = ref([
  { name: t('management.tour.deleted').toUpperCase(), value: true },
  { name: t('management.tour.none').toUpperCase(), value: false }
]);
const isLoading = ref(false);
const dataTableTourDetailRef = ref();

const frozenFields = ref(['svId', 'ttsId', `tourName${langCapitalize}`]);
const columnFields = ref([
  { field: 'svId', header: t('management.tour.list_tour_detail.sv_id') },
  { field: 'ttsId', header: t('management.tour.list_tour_detail.tts_id') },
  { field: `tourName${langCapitalize}`, header: t('management.tour.list_tour_detail.tour_name'), fieldOther: getTextVariant(currentLanguage, 'tourNameFr', 'tourNameEn') },
  { field: `tourInclusion${langCapitalize}`, header: t('management.tour.list_tour_detail.tour_inclusions'), fieldOther: getTextVariant(currentLanguage, 'tourInclusionFr', 'tourInclusionEn') },
  { field: `tourDuration${langCapitalize}`, header: t('management.tour.list_tour_detail.tour_duration'), fieldOther: getTextVariant(currentLanguage, 'tourDurationFr', 'tourDurationEn') },
  { field: `tourType`, header: t('management.tour.list_tour_detail.tour_type') },
  { field: `guided`, header: t('management.tour.list_tour_detail.guided') },
  { field: `visitedCities${langCapitalize}`, header: t('management.tour.list_tour_detail.visited_cities'), fieldOther: getTextVariant(currentLanguage, 'visitedCitiesFr', 'visitedCitiesEn') },
  { field: 'imageLink', header: t('management.tour.list_tour_detail.image_link') },
  { field: `tourLink${langCapitalize}`, header: t('management.tour.list_tour_detail.tour_details_link'), fieldOther: getTextVariant(currentLanguage, 'tourLinkFr', 'tourLinkEn') },
  { field: `tourDescription${langCapitalize}`, header: t('management.tour.list_tour_detail.tours_description'), fieldOther: getTextVariant(currentLanguage, 'tourDescriptionFr', 'tourDescriptionEn') },
  { field: 'destinationCode', header: t('management.tour.list_tour_detail.destination_code') },
  { field: 'maxRooms', header: t('management.tour.list_tour_detail.max_rooms') },
  { field: 'quoteDate', header: t('management.tour.list_tour_detail.quote_dates') },
  { field: 'travelDate', header: t('management.tour.list_tour_detail.travel_dates') },
  { field: 'blackoutDate', header: t('management.tour.list_tour_detail.blackout_dates') },
  { field: 'groupTypes', header: t('management.tour.list_tour_detail.group_types') },
  { field: 'tourApiCallOrder', header: t('management.tour.list_tour_detail.tour_api_call_order') },
  { field: 'cacheType', header: t('management.tour.list_tour_detail.cache_type') },
  { field: 'instaQuote', header: t('management.tour.list_tour_detail.insta_quote') },
  { field: 'status', header: t('management.tour.list_tour_detail.status') },
  { field: 'notes', header: t('management.tour.list_tour_detail.notes') },
  { field: 'deleteStatus', header: t('management.tour.deleted_status') },
  { field: 'action', header: t('management.tour.list_tour_detail.action') }
]);

const formatShowFieldColumn = (isDisplayColumn?: boolean) => {
  const filterColumn = formatColumnExport(columnFields.value, currentLanguage);
  filterColumn.push({ field: 'tourOnly', header: t('management.tour.tour_only') });
  return !isDisplayColumn ? filterColumn : [...filterColumn, { field: 'validateMessage', header: t('management.hotel.message') }];
};

const columns = ref(props.isView ? formatShowFieldColumn(true) : columnFields.value);
const globalFilterFields = ref([
  'svId',
  'ttsId',
  `tourName${langCapitalize}`,
  `tourInclusion${langCapitalize}`,
  `tourDuration${langCapitalize}`,
  'imageLink',
  `tourLink${langCapitalize}`,
  `tourDescription${langCapitalize}`,
  'destinationCode',
  'maxRooms',
  'quoteDate',
  'travelDate',
  'blackoutDate',
  'groupTypes',
  'tourApiCallOrder',
  `tourType`,
  'cacheType',
  'status',
  'instaQuote',
  'deleteStatus'
]);
const filtersTour = ref();
const sheetsDataTour = ref<any>();
const widthList = [12, 12, 50, 50, 20, 20, 20, 20, 20, 20, 40, 40, 40, 40, 40, 40, 40, 15, 10, 20, 20, 20, 20, 20, 20, 10, 10];
const sheetColumnStyle = styleColumnExport(widthList);
const tourDetailsDataCpt = computed(() => props.tourDetailsData);
const urlUpload = computed(() => tourManagementStore.getUrlUploadFileTour);
const isProgress = ref(false);
const isShowModal = ref(false);
const isSuccess = ref(false);
const isRevert = ref(false);
const sortOrder = ref(0);
const sortField = ref('validateMessage');
const isSort = ref(false);

FilterService.register('filterDates', (dates, filterValue) => {
  const datesFormat = handleDisplayDetailDate(dates, ' ');
  return !filterValue || datesFormat.includes(filterValue);
});

const initFilters = () => {
  filtersTour.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    svId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    ttsId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    [`tourName${langCapitalize}`]: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    [`tourInclusion${langCapitalize}`]: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    [`tourDuration${langCapitalize}`]: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    imageLink: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    [`tourLink${langCapitalize}`]: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    [`tourDescription${langCapitalize}`]: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destinationCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    maxRooms: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    quoteDate: { value: null, matchMode: 'filterDates' },
    blackoutDate: { value: null, matchMode: 'filterDates' },
    travelDate: { value: null, matchMode: 'filterDates' },
    groupTypes: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    tourApiCallOrder: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    tourType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    cacheType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    status: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    instaQuote: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    deleteStatus: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] }
  };
};
initFilters();

const handleDisplayDetailDate = (listDetailDate: any, separator = '\n') => {
  let formatDetailDate = '';
  if (listDetailDate.length) {
    formatDetailDate = listDetailDate.map((item: any) => `${formatDate(item.start, 'dd-LLL-yyyy', true, currentLanguage)} to ${formatDate(item.end, 'dd-LLL-yyyy', true, currentLanguage)}`).join(separator);
  }
  return formatDetailDate;
};

const getOrderLabel = (apiOrder: any, slotPropsValue: any) => {
  return apiOrder.find((type: any) => type.value === slotPropsValue)?.label;
};

const isFrozenField = (columnField: string) => {
  return frozenFields.value.includes(columnField);
};

const convertListGroupType = (groupTypes: any) => {
  const configList = props.groupTypeList?.filter((item: any) => groupTypes?.includes(item.sk));
  return configList?.reduce((prev: any, cur: any) => {
    if (!prev) {
      return currentLanguage === LANG_ENGLISH ? cur.name : cur.nameFr;
    }
    return prev + ', ' + (currentLanguage === LANG_ENGLISH ? cur.name : cur.nameFr);
  }, '');
};

const getSeverity = (status: string) => {
  switch (status) {
    case STATUS_OPEN:
      return 'info';
    case STATUS_CLOSED:
      return 'danger';
    default:
      return '';
  }
};

const isShowDropdownFilter = (field: string) => {
  const fields = ['instaQuote', 'status'];
  return fields.includes(field);
};

const openEditTourModal = (data: any) => {
  const dialogRef = dialog.open(AddManualTourModal, {
    props: {
      header: t('management.tour.edit_manual'),
      modal: true,
      style: {
        width: '85vw'
      }
    },
    data: {
      isEdit: true,
      data
    },
    emits: {
      onApply: () => {
        emits('initTableData');
        dialogRef.close();
      },
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const handleDeleteTourDetail = (data: any) => {
  const payload = {
    sk: data.sk,
    pk: data.pk
  };
  tourManagementStore
    .deleteTourDetail(payload)
    .then(() => {
      emits('initTableData');
      toast.add({ severity: 'success', summary: '', detail: t('notification.delete_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.delete_error'), life: 5000 });
    });
};

const clearFilter = () => {
  initFilters();
};

const getDeleteSeverity = (status: string) => {
  if (!status) {
    return 'info';
  } else {
    return 'danger';
  }
};

const isUseDataType = (field: string) => {
  if (['quoteDate', 'blackoutDate', 'travelDate'].includes(field)) {
    return 'date';
  }
  return 'text';
};

const checkMatchMode = (dataField: string) => {
  return !['blackoutDate', 'travelDate', 'quoteDate', 'deleteStatus', 'durations'].includes(dataField);
};

const openSeeMore = (dataField: string, header: string) => {
  const dialogRef = dialog.open(SeeMoreToursDescription, {
    props: {
      header: `${header}`,
      modal: true,
      style: {
        width: '65vw'
      }
    },
    data: {
      dataField: dataField
    },
    emits: {
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const isCustomMinWidth = (dataField: string) => {
  return ['quoteDate', 'blackoutDate', 'travelDate'].includes(dataField);
};

const filterDataTourOnly = () => {
  return props.isView ? props.tourDetailsData : props.tourDetailsData.filter((tour: any) => !!tour.tourOnly);
};

const isUseBreakAll = (dataField: string) => {
  return ['svId', 'ttsId'].includes(dataField);
};

const handleDataExport = (listData: any) => {
  return listData.map((data: any) => {
    return {
      ...data,
      blackoutDate: handleDisplayDetailDate(data.blackoutDate, ', '),
      travelDate: handleDisplayDetailDate(data.travelDate, ', '),
      quoteDate: handleDisplayDetailDate(data.quoteDate, ', '),
      groupTypes: convertListGroupType(data.groupTypes),
      tourApiCallOrder: getOrderLabel(apiOrder.value, data.tourApiCallOrder)
    };
  });
};

const handleApplyImportFile = (file: any) => {
  isProgress.value = true;
  isShowModal.value = true;
  isRevert.value = false;
  const payload = new FormData();
  payload.append('file', file);
  tourManagementStore
    .importTourDetail(payload)
    .then(() => {
      isSuccess.value = true;
      emits('initTableData');
    })
    .catch(() => {
      isSuccess.value = false;
    })
    .finally(() => {
      isProgress.value = false;
    });
};

const handleApplyRevertFile = (file: any) => {
  isRevert.value = true;
  isProgress.value = true;
  isShowModal.value = true;
  tourManagementStore
    .revertFileTourDetail(file)
    .then(() => {
      isSuccess.value = true;
      emits('initTableData');
    })
    .catch(() => {
      isSuccess.value = false;
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isProgress.value = false;
    });
};

const showPreviewImport = ({ data, file }: any, type: string) => {
  dialog.open(ViewDataTourDetailModal, {
    props: {
      header: t('common.labels.review_upload_file'),
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      tourDetailsData: data,
      groupTypeList: props.groupTypeList,
      type: type
    },
    emits: {
      onApply: () => {
        if (type === 'IMPORT') {
          handleApplyImportFile(file);
        } else if (type === 'REVERT') {
          handleApplyRevertFile(file);
        }
      },
      handleReOpenModal: () => {
        handleShowRevertVersion();
      }
    }
  });
};

const handleCloseModalProgress = (value: boolean) => {
  if (isRevert.value) {
    handleShowRevertVersion();
  }
  isShowModal.value = value;
};

watch(
  tourDetailsDataCpt,
  (newVal) => {
    sheetsDataTour.value = [
      {
        name: 'Sheet 1',
        data: formatDataExport(handleDataExport(newVal), formatShowFieldColumn())
      }
    ];
  },
  { immediate: true, deep: true }
);

const handleShowMessage = (message: string) => {
  return message ?? t('common.labels.success');
};

const sortMessage = () => {
  isSort.value = !isSort.value;
  sortOrder.value = isSort.value ? -1 : 0;
};

const rowClass = (data: any) => {
  return [{ 'bg-error': data?.validateMessage }];
};

const handleShowRevertVersion = () => {
  isLoading.value = true;
  tourManagementStore
    .getVersionRevertTourGateDes('TOUR')
    .then((response) => {
      openModalRevertFile(response.data);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const openModalRevertFile = (versions: any) => {
  dialog.open(VersionRevertTourDetail, {
    props: {
      header: t('common.buttons.revert'),
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      versions
    },
    emits: {
      onSelectVersion: (data: any) => {
        showPreviewImport({ data: data.tourDetailsData, file: data.file }, data.type);
      }
    }
  });
};

const formatOnlyTour = (dataField: any) => {
  if (dataField === true) {
    return 'TRUE';
  }
  if (dataField === false) {
    return 'FALSE';
  }
  return dataField;
};

// Temporary fix due to issue in PrimeVue 4.2.5 for frozen columns
// https://github.com/primefaces/primevue/issues/6067
// To be removed as soon as we have a fix
const frozenTableDetailTour = () => {
  const tableTours = dataTableTourDetailRef.value?.$el.querySelector('.p-datatable-thead');
  if (tableTours) {
    const colWidths = tableTours.querySelectorAll('th');
    document.documentElement.style.setProperty('--tour-detail-width-1', 0 + 'px');
    document.documentElement.style.setProperty('--tour-detail-width-2', colWidths[0].offsetWidth + 'px');
    document.documentElement.style.setProperty('--tour-detail-width-3', colWidths[0].offsetWidth + colWidths[1].offsetWidth + 'px');
  }
};

onMounted(() => {
  frozenTableDetailTour();
});

defineExpose({
  handleDataExport,
  formatShowFieldColumn,
  sheetColumnStyle
});
</script>
<template>
  <div class="tour-detail-list">
    <LoadingSpinner v-if="isLoading" />
    <DataTable
      editMode="cell"
      :value="filterDataTourOnly()"
      ref="dataTableTourDetailRef"
      :scrollHeight="isView ? '500px' : '900px'"
      sortMode="single"
      :sortOrder="sortOrder"
      scrollable
      v-model:filters="filtersTour"
      paginator
      :rows="rowsPerPageOptions[0]"
      :rowsPerPageOptions="rowsPerPageOptions"
      showGridlines
      filterDisplay="menu"
      dataKey="ttsId"
      :globalFilterFields="globalFilterFields"
      :sortField="sortField"
      :rowClass="rowClass"
    >
      <template #empty>{{ $t('management.tour.list_tour_detail.fare_not_available') }}</template>
      <template #header>
        <div class="flex justify-content-between flex-col sm:flex-row">
          <ActionButtons
            :exportRole="authorizations.tour_detail_configuration.export_tour_detail && !isView"
            :importRole="authorizations.tour_detail_configuration.import_tour_detail && !isView"
            :revertRole="authorizations.tour_detail_configuration.revert_tour_detail && !isView"
            :sheetsData="sheetsDataTour"
            :sheetColumnStyle="sheetColumnStyle"
            :fileName="renderNameFileExport('Tour_Details_Management')"
            :urlUpload="urlUpload"
            :importLabel="$t('management.tour.upload_tour_details')"
            @clearFilter="clearFilter()"
            @onApply="(data) => showPreviewImport(data, 'IMPORT')"
            @showRevertVersion="handleShowRevertVersion()"
          ></ActionButtons>
          <span class="p-input-icon-left">
            <i class="pi pi-search" />
            <InputText v-model="filtersTour['global'].value" :placeholder="$t('my_quote.search_keyword')" />
          </span>
        </div>
      </template>
      <TableColumn
        :show-filter-operator="false"
        :show-add-button="false"
        v-for="column of columns"
        :key="column.field"
        :field="column.field"
        :frozen="isFrozenField(column.field)"
        :showFilterMatchModes="checkMatchMode(column.field)"
        :dataType="isUseDataType(column.field)"
        :sortable="column.field === 'validateMessage'"
        :class="{
          'm-w-300': column.field === 'groupTypes' || column.field === 'validateMessage',
          'm-w-140': isCustomMinWidth(column.field),
          'm-w-150': column.field === 'guided',
          'text-link-style': column.field === 'imageLink' || column.field === `tourLink${langCapitalize}`,
          'break-all-style': isUseBreakAll(column.field)
        }"
      >
        <template #header>
          <span :class="{ 'flex justify-content-center gap-3 align-content-center w-full': true }">
            {{ column.header }}
          </span>
        </template>
        <template #body="{ data, field }">
          <span v-if="field === 'quoteDate' || field === 'blackoutDate' || field === 'travelDate'">
            {{ handleDisplayDetailDate(data[field]) }}
          </span>
          <span v-else-if="field === 'status'">
            <ToggleSwitch disabled v-model="data[field]" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" />
          </span>
          <span v-else-if="field === 'instaQuote'">
            <ToggleSwitch disabled v-model="data[field]" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" />
          </span>
          <span v-else-if="field === 'notes'">
            <TourNotes :data="data" :groupTypeList="groupTypeList"></TourNotes>
          </span>
          <span v-else-if="field === 'groupTypes'">
            {{ convertListGroupType(data[field]) }}
          </span>
          <span v-else-if="(field === 'guided' || field === 'tourType') && isView">
            {{ handleReplaceValueToName(data[field], field) }}
          </span>
          <span v-else-if="field === 'tourApiCallOrder'">
            <Select disabled class="table-select w-full" :options="apiOrder" optionLabel="label" optionValue="value" v-model="data[field]">
              <template #value="slotProps">
                <div v-if="slotProps.value">
                  <div class="overflow-hidden text-overflow-ellipsis">{{ getOrderLabel(apiOrder, slotProps.value) }}</div>
                </div>
              </template>
            </Select>
          </span>
          <span v-else-if="field === 'deleteStatus'">
            <div class="flex flex-col">
              <span> {{ data[field] ? $t('management.tour.deleted') : '' }} </span>
            </div>
          </span>
          <span v-else-if="field === 'action'">
            <div class="flex">
              <Button v-if="authorizations.tour_detail_configuration.edit_tour_detail_manually" icon="pi pi-pencil" class="p-button-text" @click="openEditTourModal(data)" />
              <DeleteButton v-if="authorizations.tour_detail_configuration.delete_tour_combination_manually" :record="data" icon="pi pi-trash" @handleDelete="(tour) => handleDeleteTourDetail(tour)" />
            </div>
          </span>
          <div v-else-if="field === `tourDescriptionEn` || field === `tourDescriptionFr` || field === `tourInclusion${langCapitalize}`">
            <div class="white-space-nowrap overflow-hidden text-overflow-ellipsis description-width">{{ data[field] }}</div>
            <span v-if="data[field] && data[field].length > maxLengthText" class="view-more">
              <a class="text-blue-600 font-bold underline cursor-pointer text-left uppercase" @click="openSeeMore(data[field], column.header)">{{ $t('common.labels.see_more') }}</a>
            </span>
          </div>
          <span v-else-if="isView && field === 'tourOnly'"> {{ formatOnlyTour(data[field]) }} </span>

          <span v-else-if="isView && field === 'validateMessage'" class="mb-0 break-spaces">{{ handleShowMessage(data[field]) }}</span>

          <span v-else> {{ data[field] }} </span>
        </template>

        <template #filter="{ filterModel }" v-if="globalFilterFields.includes(column.field)">
          <Select v-if="isShowDropdownFilter(column.field)" v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
            <template #option="slotProps">
              <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
            </template>
          </Select>
          <Select v-else-if="column.field === 'deleteStatus'" v-model="filterModel.value" optionLabel="name" optionValue="value" :options="deletedStatuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
            <template #option="slotProps">
              <Tag :value="slotProps.option.name" :severity="getDeleteSeverity(slotProps.option.value)" />
            </template>
          </Select>
          <Select v-else-if="column.field === 'groupTypes'" v-model="filterModel.value" :options="groupTypeList" optionLabel="name" optionValue="sk" :placeholder="$t('common.select_one')" class="p-column-filter w-20rem" showClear>
            <template #option="slotProps">
              <div class="max-w-20rem overflow-hidden text-overflow-ellipsis" v-tooltip.left="getTextVariant(currentLanguage, slotProps.option['name'], slotProps.option['nameFr'])">
                {{ getTextVariant(currentLanguage, slotProps.option['name'], slotProps.option['nameFr']) }}
              </div>
            </template>
          </Select>
          <Select v-else-if="column.field === 'tourApiCallOrder'" v-model="filterModel.value" :options="apiOrder" optionLabel="label" optionValue="value" :placeholder="$t('common.select_one')" class="p-column-filter table-select w-full" showClear>
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <div class="w-6rem overflow-hidden text-overflow-ellipsis">{{ apiOrder.find((type) => type.value === slotProps.value)?.label }}</div>
              </div>
            </template>
          </Select>
          <InputText v-else v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="column.header" />
        </template>
        <template #sorticon>
          <i class="ml-2 sort-icon" :class="sortOrder === -1 ? 'pi pi-sort-amount-down-alt' : 'pi pi-sort-amount-up-alt'" @click="sortMessage()"></i>
        </template>
      </TableColumn>
    </DataTable>
    <Dialog v-model:visible="isShowModal" :header="$t('management.tour.uploading_tour_details')" modal :closable="!isProgress">
      <ProgressModal :isProgress="isProgress" @handleClose="(value) => handleCloseModalProgress(value)" :isSuccess="isSuccess" :isRevert="isRevert" />
    </Dialog>
  </div>
</template>
